import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getDefaultPeriod } from "components/common/PeriodPicker/periodUtils";
import TeamStatCard from "./TeamStatCard";
import AttendancePlayerList from "./AttendancePlayerList";
import AttendanceBarChart from "./AttendanceBarChart";
import AttendanceGrid from "./AttendanceGrid";
import AttendancePlayerDetail from "./AttendancePlayerDetail";
import betaAPI from "v2/api";
import showToast from "utils/toast";
import PaneFrame from "../common/PaneFrame";
import { dateLocale } from "i18n";

const formatShortLabel = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString(dateLocale("en-GB"), {
    day: "numeric",
    month: "short",
  });
};

const AttendanceTab = ({ players, team }) => {
  const { t } = useTranslation();
  const { workspaceId, teamId } = useParams();

  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [sessionTypeFilter, setSessionTypeFilter] = useState("all");
  const [period, setPeriod] = useState(() => getDefaultPeriod());

  // Build date query params
  const dateParams = useMemo(() => {
    const params = {};
    if (period?.startDate) {
      params.start_date = period.startDate.toISOString().split("T")[0];
    }
    if (period?.endDate) {
      params.end_date = period.endDate.toISOString().split("T")[0];
    }
    return params;
  }, [period]);

  // Fetch attendance data
  const fetchAttendance = useCallback(
    async ({ silent = false } = {}) => {
      if (!workspaceId || !teamId) return;
      try {
        if (!silent) setLoading(true);
        const res = await betaAPI.getTeamAttendance({
          workspaceId,
          teamId,
          queryParams: dateParams,
        });
        setAttendanceData(res.data);
      } catch (err) {
        console.error("Failed to fetch attendance", err);
        showToast.error(t("team.attendanceTab.loadFailed"));
      } finally {
        setLoading(false);
      }
    },
    [workspaceId, teamId, dateParams, t]
  );

  const initialLoadDone = useRef(false);

  useEffect(() => {
    if (initialLoadDone.current) {
      fetchAttendance({ silent: true });
    } else {
      fetchAttendance().then(() => {
        initialLoadDone.current = true;
      });
    }
  }, [fetchAttendance]);

  // Build attendance lookup set
  const attendanceRecords = attendanceData?.attendance || [];
  const attendanceSet = useMemo(
    () =>
      new Set(attendanceRecords.map((a) => `${a.player_id}:${a.session_id}`)),
    [attendanceRecords]
  );

  // Sessions from attendance response
  const allSessions = useMemo(
    () => attendanceData?.sessions || [],
    [attendanceData]
  );

  // Filter sessions by type
  const filteredSessions = useMemo(() => {
    if (sessionTypeFilter === "all") return allSessions;
    return allSessions.filter(
      (s) =>
        s.type === sessionTypeFilter || s.session_type === sessionTypeFilter
    );
  }, [allSessions, sessionTypeFilter]);

  // Per-player attendance stats
  const playerStats = useMemo(() => {
    return players
      .map((player) => {
        const present = filteredSessions.filter((s) =>
          attendanceSet.has(`${player.id}:${s.id}`)
        ).length;
        const rate =
          filteredSessions.length > 0
            ? Math.round((present / filteredSessions.length) * 100)
            : 0;
        return {
          ...player,
          present,
          absent: filteredSessions.length - present,
          rate,
        };
      })
      .sort((a, b) => b.rate - a.rate);
  }, [players, filteredSessions, attendanceSet]);

  // Per-session stats for bar chart
  const sessionStats = useMemo(() => {
    return allSessions.map((s) => ({
      ...s,
      shortLabel: formatShortLabel(s.date || s.created_at),
      presentCount: players.filter((p) => attendanceSet.has(`${p.id}:${s.id}`))
        .length,
      totalPlayers: players.length,
    }));
  }, [allSessions, players, attendanceSet]);

  // Summary stats
  const summaryStats = useMemo(() => {
    const avgRate =
      playerStats.length > 0
        ? Math.round(
            playerStats.reduce((sum, p) => sum + p.rate, 0) / playerStats.length
          )
        : 0;

    const trainingCount = filteredSessions.filter(
      (s) => s.type === "training" || s.session_type === "training"
    ).length;
    const matchCount = filteredSessions.filter(
      (s) => s.type === "match" || s.session_type === "match"
    ).length;

    const fullTurnouts = sessionStats.filter(
      (s) =>
        s.presentCount === s.totalPlayers &&
        s.totalPlayers > 0 &&
        (sessionTypeFilter === "all" ||
          s.type === sessionTypeFilter ||
          s.session_type === sessionTypeFilter)
    ).length;

    return {
      avgRate,
      sessionCount: filteredSessions.length,
      trainingCount,
      matchCount,
      fullTurnouts,
      playerCount: players.length,
    };
  }, [playerStats, filteredSessions, sessionStats, players, sessionTypeFilter]);

  // Selected player
  const selectedPlayer = playerStats.find((p) => p.id === selectedPlayerId);

  // Player's session list with present boolean
  const selectedPlayerSessions = useMemo(() => {
    if (!selectedPlayerId) return [];
    return filteredSessions.map((s) => ({
      ...s,
      present: attendanceSet.has(`${selectedPlayerId}:${s.id}`),
    }));
  }, [selectedPlayerId, filteredSessions, attendanceSet]);

  // Toggle player selection
  const handlePlayerSelect = (player) => {
    setSelectedPlayerId(player.id === selectedPlayerId ? null : player.id);
  };

  // Loading skeleton — same frame as the loaded tab so nothing shifts.
  if (loading) {
    return (
      <PaneFrame
        rail={
          <div className="flex h-full flex-col rounded-xl border border-[#F3F4F6] bg-white">
            <div className="flex animate-pulse items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
              <div className="h-4 w-16 rounded bg-[#E5E7EB]" />
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="flex animate-pulse items-center gap-3 border-b border-[#F3F4F6] px-4 py-3"
                >
                  <div className="h-9 w-9 rounded-full bg-[#E5E7EB]" />
                  <div className="flex-1">
                    <div className="mb-1 h-3 w-24 rounded bg-[#E5E7EB]" />
                    <div className="h-2.5 w-16 rounded bg-[#E5E7EB]" />
                  </div>
                  <div className="h-3 w-12 rounded bg-[#E5E7EB]" />
                </div>
              ))}
            </div>
          </div>
        }
      >
        <div className="grid grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded-xl border border-[#F3F4F6] bg-white p-4"
            >
              <div className="mb-2 h-3 w-20 rounded bg-[#E5E7EB]" />
              <div className="mb-2 h-8 w-16 rounded bg-[#E5E7EB]" />
              <div className="h-3 w-full rounded bg-[#E5E7EB]" />
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-[#F3F4F6] bg-white">
          <div className="flex animate-pulse items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
            <div className="h-4 w-32 rounded bg-[#E5E7EB]" />
          </div>
          <div className="p-4">
            <div className="h-[200px] w-full animate-pulse rounded-xl bg-[#E5E7EB]" />
          </div>
        </div>
        <div className="h-48 animate-pulse rounded-xl border border-[#F3F4F6] bg-[#E5E7EB]" />
      </PaneFrame>
    );
  }

  return (
    <PaneFrame
      rail={
        <AttendancePlayerList
          players={playerStats}
          selectedPlayerId={selectedPlayerId}
          onPlayerSelect={handlePlayerSelect}
        />
      }
    >
      {/* 4 stat cards — full width of the pane */}
      <div className="grid grid-cols-4 gap-3">
        <TeamStatCard
          label={t("team.attendanceTab.avgAttendance")}
          value={`${summaryStats.avgRate}%`}
          footerLeft={t("team.attendanceTab.teamAverage")}
          footerRight={t("team.attendanceTab.acrossSessions", {
            value: summaryStats.sessionCount,
          })}
        />
        <TeamStatCard
          label={t("team.impactLoad.stats.sessions")}
          value={summaryStats.sessionCount}
          footerLeft={t("team.impactLoad.stats.inSelectedPeriod")}
          footerRight={t("team.attendanceTab.trainingMatchBreakdown", {
            training: summaryStats.trainingCount,
            matches: summaryStats.matchCount,
          })}
        />
        <TeamStatCard
          label={t("team.attendanceTab.fullTurnouts")}
          value={summaryStats.fullTurnouts}
          footerLeft={t("team.attendanceTab.fullAttendance")}
          footerRight={t("team.attendanceTab.ofSessions", {
            value: summaryStats.sessionCount,
          })}
        />
        <TeamStatCard
          label={t("team.attendanceTab.athletesTracked")}
          value={summaryStats.playerCount}
          footerLeft={t("team.attendanceTab.onTeamRoster")}
          footerRight={team?.name || ""}
        />
      </div>

      <AttendanceBarChart
        sessions={sessionStats}
        sessionTypeFilter={sessionTypeFilter}
        onSessionTypeChange={setSessionTypeFilter}
        period={period}
        setPeriod={setPeriod}
        totalPlayers={players.length}
      />
      {selectedPlayer ? (
        <AttendancePlayerDetail
          player={selectedPlayer}
          sessions={selectedPlayerSessions}
        />
      ) : (
        <AttendanceGrid
          players={playerStats}
          sessions={filteredSessions}
          attendance={attendanceRecords}
          onPlayerSelect={handlePlayerSelect}
          selectedPlayerId={selectedPlayerId}
        />
      )}
    </PaneFrame>
  );
};

export default AttendanceTab;
